import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

function formatDate(value) {
  if (!value) return 'Never'
  try { return new Intl.DateTimeFormat('el-GR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) } catch { return value }
}

function Stat({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-50 text-slate-700',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    amber: 'bg-amber-50 text-amber-700'
  }
  return <div className={`rounded-xl p-4 ${tones[tone]}`}><div className="text-2xl font-semibold">{value}</div><div className="mt-1 text-xs uppercase tracking-wide opacity-70">{label}</div></div>
}

export default function CalendarSyncManager() {
  const [connections, setConnections] = useState([])
  const [links, setLinks] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    load()
    const onMessage = (event) => {
      if (event?.data?.type !== 'codevaro-google-calendar-oauth') return
      setConnecting(false)
      if (event.data.ok) {
        setMessage('Google Calendar connected successfully.')
        load()
      } else {
        setError('Google Calendar connection was not completed.')
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  async function load() {
    try {
      setLoading(true); setError('')
      const [c, l] = await Promise.all([
        supabase.from('codevaro_calendar_connections').select('id,provider,account_email,calendar_id,calendar_name,sync_mode,is_active,last_sync_at,notes,token_updated_at,created_at,updated_at').order('created_at'),
        supabase.from('codevaro_job_calendar_links').select('id,job_id,calendar_id,external_event_id,external_event_url,sync_status,last_synced_at,last_error,created_at,codevaro_jobs(title,appointment_start,client_id,codevaro_clients(name))').eq('provider', 'google').order('created_at', { ascending: false }).limit(200)
      ])
      if (c.error) throw c.error
      if (l.error) throw l.error
      setConnections(c.data || [])
      setLinks(l.data || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const counts = useMemo(() => {
    const out = { pending: 0, synced: 0, error: 0, other: 0 }
    links.forEach((x) => { if (out[x.sync_status] !== undefined) out[x.sync_status] += 1; else out.other += 1 })
    return out
  }, [links])

  async function connectGoogle() {
    try {
      setConnecting(true); setError(''); setMessage('')
      const { data, error: invokeError } = await supabase.functions.invoke('codevaro-google-calendar-oauth-start', { body: {} })
      if (invokeError) {
        const context = invokeError.context
        let details = ''
        try { details = context ? await context.text() : '' } catch {}
        throw new Error(details || invokeError.message)
      }
      if (!data?.auth_url) throw new Error('Google OAuth URL was not returned by the server.')
      const popup = window.open(data.auth_url, 'codevaro-google-calendar', 'width=640,height=760,menubar=no,toolbar=no,location=yes,status=no')
      if (!popup) throw new Error('Your browser blocked the Google sign-in popup. Allow popups for Codevaro and try again.')
      popup.focus()
    } catch (e) {
      setConnecting(false)
      let msg = e.message || String(e)
      try {
        const parsed = JSON.parse(msg)
        if (parsed?.required_secrets?.length) msg = `Google OAuth is not configured yet. Required server secrets: ${parsed.required_secrets.join(', ')}`
        else if (parsed?.error) msg = parsed.error
      } catch {}
      setError(msg)
    }
  }

  async function syncAll() {
    try {
      setSyncing(true); setError(''); setMessage('')
      const { data, error: invokeError } = await supabase.functions.invoke('codevaro-google-calendar-sync', { body: {} })
      if (invokeError) {
        const context = invokeError.context
        let details = ''
        try { details = context ? await context.text() : '' } catch {}
        throw new Error(details || invokeError.message)
      }
      if (data?.configured === false) {
        setMessage(data?.needs_connection ? 'Google Calendar is not connected yet. Click Connect Google.' : `Google OAuth is not configured yet. Required server secrets: ${(data.required_secrets || []).join(', ')}`)
      } else {
        setMessage(`Sync processed ${data?.processed ?? 0} job(s).`)
      }
      await load()
    } catch (e) {
      let msg = e.message || String(e)
      try {
        const parsed = JSON.parse(msg)
        if (parsed?.error) msg = parsed.error
      } catch {}
      setError(msg)
    } finally {
      setSyncing(false)
    }
  }

  if (loading) return <div className="flex h-64 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" /></div>

  const connected = connections.some((x) => x.is_active && x.token_updated_at)

  return <div className="space-y-6">
    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <div><h1 className="text-2xl font-semibold text-slate-900">Google Calendar Sync</h1><p className="mt-1 text-sm text-slate-600">Connect Google Calendar and sync Codevaro job appointments.</p></div>
      <div className="flex flex-wrap gap-2">
        <button onClick={connectGoogle} disabled={connecting} className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"><SafeIcon icon={FiIcons.FiLink} className="h-4 w-4" />{connecting ? 'Opening Google...' : connected ? 'Reconnect Google' : 'Connect Google'}</button>
        <button onClick={syncAll} disabled={syncing || !connected} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"><SafeIcon icon={FiIcons.FiRefreshCw} className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : 'Sync pending jobs'}</button>
      </div>
    </div>

    <div className={`rounded-xl border p-4 text-sm ${connected ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
      <div className="flex items-start gap-2"><SafeIcon icon={connected ? FiIcons.FiCheckCircle : FiIcons.FiAlertCircle} className="mt-0.5 h-5 w-5 shrink-0" /><div><strong>{connected ? 'Google Calendar connected.' : 'Google Calendar needs authorization.'}</strong><div className="mt-1">{connected ? 'Codevaro can create and update Google Calendar events for jobs with Calendar sync enabled.' : 'Click Connect Google and approve Calendar access once. The refresh token is stored server-side and is not exposed to the browser.'}</div></div></div>
    </div>

    {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}
    {message ? <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">{message}</div> : null}

    <div className="grid grid-cols-2 gap-3 md:grid-cols-4"><Stat label="Pending" value={counts.pending} tone="amber" /><Stat label="Synced" value={counts.synced} tone="green" /><Stat label="Errors" value={counts.error} tone="red" /><Stat label="Other" value={counts.other} tone="slate" /></div>

    <div className="space-y-4">{connections.length ? connections.map((connection) => <section key={connection.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"><div><div className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiCalendar} className="h-5 w-5 text-blue-600" /><h2 className="text-lg font-semibold text-slate-900">{connection.calendar_name || 'Google Calendar'}</h2></div><p className="mt-1 break-all font-mono text-xs text-slate-500">{connection.calendar_id || 'No calendar ID'}</p></div><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${connection.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>{connection.is_active ? 'Active' : 'Inactive'} · {connection.sync_mode}</span></div>
      <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-4"><div><div className="text-xs uppercase text-slate-400">Account</div><div>{connection.account_email || '—'}</div></div><div><div className="text-xs uppercase text-slate-400">OAuth connected</div><div>{formatDate(connection.token_updated_at)}</div></div><div><div className="text-xs uppercase text-slate-400">Last sync</div><div>{formatDate(connection.last_sync_at)}</div></div><div><div className="text-xs uppercase text-slate-400">Provider</div><div>{connection.provider}</div></div></div>
    </section>) : <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">No calendar connection configured.</div>}</div>

    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="mb-4 flex items-center justify-between"><h2 className="font-semibold text-slate-900">Job sync queue</h2><button onClick={load} className="text-sm text-blue-600">Refresh</button></div>{links.length ? <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead className="text-left text-xs uppercase text-slate-500"><tr><th className="pb-2 pr-4">Job</th><th className="pb-2 pr-4">Appointment</th><th className="pb-2 pr-4">Status</th><th className="pb-2 pr-4">Last sync</th><th className="pb-2">Google</th></tr></thead><tbody>{links.map(link => <tr key={link.id} className="border-t border-slate-100"><td className="py-3 pr-4"><div className="font-medium text-slate-900">{link.codevaro_jobs?.title || 'Job'}</div><div className="text-xs text-slate-500">{link.codevaro_jobs?.codevaro_clients?.name || '—'}</div></td><td className="py-3 pr-4">{formatDate(link.codevaro_jobs?.appointment_start)}</td><td className="py-3 pr-4"><span className={`rounded-full px-2.5 py-1 text-xs ${link.sync_status === 'synced' ? 'bg-green-100 text-green-800' : link.sync_status === 'error' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{link.sync_status}</span>{link.last_error ? <div className="mt-1 max-w-sm text-xs text-red-600">{link.last_error}</div> : null}</td><td className="py-3 pr-4">{formatDate(link.last_synced_at)}</td><td className="py-3">{link.external_event_url ? <a href={link.external_event_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600"><SafeIcon icon={FiIcons.FiExternalLink} className="h-4 w-4" />Open</a> : '—'}</td></tr>)}</tbody></table></div> : <div className="text-sm text-slate-500">No jobs are currently queued for Google Calendar.</div>}</section>
  </div>
}